
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Message} from './Message';
import {FieldModelMessage} from './Message';
import {RejectCode} from './RejectCode';
import {FieldModelRejectCode} from './RejectCode';
import {RejectInfoCode} from './RejectInfoCode';
import {FieldModelRejectInfoCode} from './RejectInfoCode';

/**
 * Reject struct
 */
class Reject extends Message {
  /**
   * Initialize struct
   * @param {!Message=} parent
   * @param {!RejectCode=} code
   * @param {!string=} reason
   * @param {!Map=} additionalInformation
   * @constructor
   */
  constructor (parent = new Message(), code = new RejectCode(), reason = '', additionalInformation = new Map()) {
    super()
    super.copy(parent)
    this.Code = code
    this.Reason = reason
    this.AdditionalInformation = additionalInformation
  }

  /**
   * Copy struct (shallow copy)
   * @this {!Reject}
   * @param {!Reject} other Other struct
   * @returns {!Reject} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Code != null) {
      this.Code = RejectCode.fromObject(other.Code)
    } else {
      this.Code = null
    }
    if (other.Reason != null) {
      this.Reason = other.Reason
    } else {
      this.Reason = null
    }
    if (other.AdditionalInformation != null) {
      this.AdditionalInformation = new Map()
      Object.keys(other.AdditionalInformation).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = RejectInfoCode.fromObject(key)
          if (other.AdditionalInformation[key] != null) {
            let tempValue
            tempValue = other.AdditionalInformation[key]
            this.AdditionalInformation.set(tempKey, tempValue)
          } else {
            this.AdditionalInformation.set(tempKey, null)
          }
        }
      })
    } else {
      this.AdditionalInformation = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!Reject}
   * @returns {!Reject} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new RejectModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new RejectModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!Reject}
   * @param {!Reject} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof Reject)) {
      throw new TypeError('Instance of Reject is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!Reject}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Code: ((this.Code != null) ? this.Code : null),
      Reason: ((this.Reason != null) ? this.Reason : null),
      AdditionalInformation: ((this.AdditionalInformation != null) ? fbe.MapToObject(new Map(Array.from(this.AdditionalInformation, item => [((item[0] != null) ? item[0] : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return Reject.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!Reject} other Object value
   * @returns {!Reject} Created struct
   */
  static fromObject (other) {
    return new Reject().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!Reject}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return Reject.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!Reject}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4
  }
}

export { Reject };

/**
 * Fast Binary Encoding Reject field model
 */
class FieldModelReject extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelMessage(buffer, 4 + 4)
    this.Code = new FieldModelRejectCode(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Reason = new fbe.FieldModelString(buffer, this.Code.fbeOffset + this.Code.fbeSize)
    this.AdditionalInformation = new fbe.FieldModelMap(new FieldModelRejectInfoCode(buffer, this.Reason.fbeOffset + this.Reason.fbeSize), new fbe.FieldModelString(buffer, this.Reason.fbeOffset + this.Reason.fbeSize), buffer, this.Reason.fbeOffset + this.Reason.fbeSize)
  }

  /**
   * Get the Message field model
   * @this {!FieldModelReject}
   * @returns {!FieldModelMessage} Message field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelReject}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelReject}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Code.fbeSize + this.Reason.fbeSize + this.AdditionalInformation.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelReject}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Code.fbeExtra + this.Reason.fbeExtra + this.AdditionalInformation.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelReject}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelReject.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelReject}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelReject}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelReject}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Code.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Code.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Code.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Reason.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize

    if ((fbeCurrentSize + this.AdditionalInformation.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AdditionalInformation.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AdditionalInformation.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelReject}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelReject}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelReject}
   * @param {!Reject} fbeValue Default value, defaults is new Reject()
   * @returns {!Reject} Reject value
   */
  get (fbeValue = new Reject()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelReject}
   * @param {!Reject} fbeValue Reject value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Code.fbeSize) <= fbeStructSize) {
      fbeValue.Code = this.Code.get()
    } else {
      fbeValue.Code = new RejectCode()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Code.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) <= fbeStructSize) {
      fbeValue.Reason = this.Reason.get()
    } else {
      fbeValue.Reason = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize

    if ((fbeCurrentSize + this.AdditionalInformation.fbeSize) <= fbeStructSize) {
      this.AdditionalInformation.get(fbeValue.AdditionalInformation)
    } else {
      fbeValue.AdditionalInformation.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AdditionalInformation.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelReject}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelReject}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelReject}
   * @param {!Reject} fbeValue Reject value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelReject}
   * @param {Reject} fbeValue Reject value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Code.set(fbeValue.Code)
    this.Reason.set(fbeValue.Reason)
    this.AdditionalInformation.set(fbeValue.AdditionalInformation)
  }
}

export { FieldModelReject };

/**
 * Fast Binary Encoding Reject model
 */
class RejectModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelReject(this.buffer, 4)
  }

  /**
   * Get the Reject model
   * @this {!RejectModel}
   * @returns {!FieldModelReject} model Reject model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!RejectModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!RejectModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return RejectModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!RejectModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelReject.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!RejectModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!RejectModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!RejectModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!RejectModel}
   * @param {!Reject} value Reject value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!RejectModel}
   * @param {!Reject} value Reject value, defaults is new Reject()
   * @return {!object} Deserialized Reject value and its size
   */
  deserialize (value = new Reject()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new Reject(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new Reject(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!RejectModel}
   * @param {!number} prev Previous Reject model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { RejectModel };
